import { useState } from 'react';

import { chartColor, isDarkMode } from './palette';

export interface DonutDatum {
    label: string;
    value: number;
    colorIndex?: number;
}

interface DonutChartProps {
    data: DonutDatum[];
    size?: number;
    thickness?: number;
    /** Center headline (defaults to the total) */
    centerLabel?: string;
    centerSub?: string;
    formatValue?: (value: number) => string;
    className?: string;
}

/**
 * Donut chart with a hero number in the center, 2px surface gaps between
 * segments, hover highlight and an always-present legend.
 */
const DonutChart: React.FC<DonutChartProps> = ({
    data,
    size = 160,
    thickness = 18,
    centerLabel,
    centerSub,
    formatValue = (v) => String(v),
    className = '',
}) => {
    const dark = isDarkMode();
    const [hovered, setHovered] = useState<number | null>(null);

    const total = data.reduce((sum, d) => sum + d.value, 0);
    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const gapPx = data.filter((d) => d.value > 0).length > 1 ? 2 : 0;

    if (total <= 0) {
        return <div className={`text-sm text-slate-400 dark:text-slate-500 text-center py-6 ${className}`}>No data yet</div>;
    }

    let offset = 0;
    const segments = data
        .map((d, i) => {
            const fraction = d.value / total;
            const length = Math.max(0, fraction * circumference - gapPx);
            const segment = { ...d, index: i, fraction, dashArray: `${length} ${circumference - length}`, dashOffset: -offset };
            offset += fraction * circumference;
            return segment;
        })
        .filter((s) => s.value > 0);

    return (
        <div className={`flex items-center gap-6 ${className}`} role="img" aria-label="Donut chart">
            <div className="relative shrink-0" style={{ width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={dark ? '#374151' : '#f3f4f6'}
                        strokeWidth={thickness}
                    />
                    {segments.map((s) => (
                        <circle
                            key={s.index}
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={chartColor(s.colorIndex ?? s.index, dark)}
                            strokeWidth={thickness}
                            strokeDasharray={s.dashArray}
                            strokeDashoffset={s.dashOffset}
                            strokeLinecap="butt"
                            transform={`rotate(-90 ${size / 2} ${size / 2})`}
                            style={{
                                opacity: hovered === null || hovered === s.index ? 1 : 0.35,
                                transition: 'opacity 150ms',
                            }}
                            onMouseEnter={() => setHovered(s.index)}
                            onMouseLeave={() => setHovered(null)}
                        />
                    ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                    <span className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                        {hovered !== null && segments.find((s) => s.index === hovered)
                            ? formatValue(segments.find((s) => s.index === hovered)!.value)
                            : (centerLabel ?? formatValue(total))}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                        {hovered !== null && segments.find((s) => s.index === hovered)
                            ? segments.find((s) => s.index === hovered)!.label
                            : (centerSub ?? 'Total')}
                    </span>
                </div>
            </div>
            <div className="space-y-1.5 min-w-0">
                {data.map((d, i) => (
                    <div
                        key={`${d.label}-${i}`}
                        className="flex items-center gap-2 text-sm cursor-default"
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered(null)}
                    >
                        <span
                            className="w-2.5 h-2.5 rounded-sm shrink-0"
                            style={{ backgroundColor: chartColor(d.colorIndex ?? i, dark) }}
                        />
                        <span className="text-slate-600 dark:text-slate-400 truncate">{d.label}</span>
                        <span className="ml-auto font-medium text-slate-900 dark:text-white tabular-nums pl-3">
                            {formatValue(d.value)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DonutChart;
